import React, { useEffect, useRef, useState } from "react";
import { Modal } from "bootstrap";
import {
  addTasks,
  logHours,
  generateReport,
  editTask,
  addSubtask,
  addSubtaskLog,
  updateSubTask,
  editTasklog,
  generateAIDesc,
} from "../api";
import EmployeeTabs, {
  type EmployeeTabsHandle,
} from "../components/Employee/EmployeeTabs";
import { useAuth } from "../context/AuthContext";
import { githubLinksFromRow } from "../utils/githubLinks";
import { completionStatusLabel } from "../utils/completionStatus";
import {
  buildCommitHashesPayload,
  buildSubtaskExtrasBody,
  commitHashesToDraftEntries,
  nonEmptyVerificationList,
  verificationEndpointsToText,
} from "../utils/subtaskPayload";
import { isDaniyalUserId } from "../utils/isDaniyalUser";
import { getDateRangeOrderError } from "../utils/dateRangeOrder";
import FinancialYearField from "../components/FinancialYearField";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { enAU } from "date-fns/locale/en-AU";
import { format } from "date-fns";
import { toast } from "react-toastify";
// Australian locale from date-fns

// Register the Australian locale
registerLocale("en-AU", enAU);
setDefaultLocale("en-AU");

/** One URL as plain string; multiple URLs as JSON array string for query-string APIs. */
function serializeGithubLinkForApi(raw: unknown): string {
  const text = String(raw ?? "").trim();
  if (!text) return "";
  const lines = text
    .split(/[\n\r]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (lines.length === 0) return "";
  if (lines.length === 1) return lines[0].slice(0, 512);
  return JSON.stringify(lines.map((l) => l.slice(0, 512)));
}

function taskGithubLinkToFormField(task: any): string {
  return githubLinksFromRow(task)
    .map((u) => u.slice(0, 512))
    .join("\n");
}

/** Same line splitting as `serializeGithubLinkForApi` uses for the textarea-era field. */
function githubLinkFieldToLines(raw: unknown): string[] {
  return String(raw ?? "")
    .split(/[\n\r]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => s.slice(0, 512));
}

function GithubLinksTagField({
  idPrefix,
  labelId,
  value,
  onChange,
}: {
  idPrefix: string;
  labelId: string;
  value: string;
  onChange: (joinedLines: string) => void;
}) {
  const lines = githubLinkFieldToLines(value);
  const [draft, setDraft] = useState("");
  const inputId = `${idPrefix}-githubLinkInput`;

  useEffect(() => {
    if (!value) setDraft("");
  }, [value]);

  const addUrlsFromDraft = () => {
    const raw = draft.trim();
    if (!raw) return;
    const pieces = raw
      .split(/[\n\r,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => s.slice(0, 512));
    const seen = new Set(lines.map((u) => u.toLowerCase()));
    const added: string[] = [];
    for (const p of pieces) {
      const k = p.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      added.push(p);
    }
    if (added.length === 0) {
      setDraft("");
      return;
    }
    onChange([...lines, ...added].join("\n"));
    setDraft("");
  };

  const removeLine = (idx: number) => {
    onChange(lines.filter((_, i) => i !== idx).join("\n"));
  };

  return (
    <div
      className="employee-github-link-tags"
      role="group"
      aria-labelledby={labelId}
    >
      {lines.map((url, idx) => {
        const href =
          /^https?:\/\//i.test(url) ? url : `https://${url.replace(/^\/+/, "")}`;
        const short =
          url.replace(/^https?:\/\//i, "").length > 44
            ? `${url.replace(/^https?:\/\//i, "").slice(0, 44)}…`
            : url.replace(/^https?:\/\//i, "");
        return (
          <span
            key={`${idx}-${url.slice(0, 24)}`}
            className="employee-github-link-tag badge bg-light text-dark border d-inline-flex align-items-center gap-1"
          >
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-truncate text-decoration-none link-secondary"
              style={{ maxWidth: "min(220px, 45vw)" }}
              title={url}
              onClick={(e) => e.stopPropagation()}
            >
              {short || url}
            </a>
            <button
              type="button"
              className="btn-close btn-close-sm"
              style={{ transform: "scale(0.7)" }}
              aria-label="Remove GitHub link"
              onClick={() => removeLine(idx)}
            />
          </span>
        );
      })}
      <div className="employee-github-link-tags-entry">
        <input
          id={inputId}
          type="text"
          className="employee-github-link-tags-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addUrlsFromDraft();
            }
          }}
          placeholder="Repo URL — Enter or + Add link"
          autoComplete="off"
        />
        {draft.trim() ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary employee-github-link-tags-add-btn"
            aria-label="Add link from field"
            onClick={() => addUrlsFromDraft()}
          >
            + Add link
          </button>
        ) : null}
      </div>
    </div>
  );
}

function normalizeCommitHashInput(raw: string): string {
  return raw.trim().replace(/\s+/g, "").slice(0, 64);
}

function SubtaskCommitHashTagsField({
  idPrefix,
  labelId,
  hashes,
  onChange,
}: {
  idPrefix: string;
  labelId: string;
  hashes: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const inputId = `${idPrefix}-commitHashInput`;

  useEffect(() => {
    if (hashes.length === 0) setDraft("");
  }, [hashes]);

  const addFromDraft = () => {
    const h = normalizeCommitHashInput(draft);
    if (!h) return;
    const seen = new Set(hashes.map((x) => x.toLowerCase()));
    if (seen.has(h.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...hashes, h]);
    setDraft("");
  };

  const removeAt = (idx: number) => {
    onChange(hashes.filter((_, i) => i !== idx));
  };

  return (
    <div
      className="employee-github-link-tags"
      role="group"
      aria-labelledby={labelId}
    >
      {hashes.map((h, idx) => (
        <span
          key={`${idx}-${h.slice(0, 16)}`}
          className="employee-github-link-tag badge bg-light text-dark border d-inline-flex align-items-center gap-1 font-monospace"
        >
          <span
            className="text-truncate"
            style={{ maxWidth: "min(220px, 45vw)" }}
            title={h}
          >
            {h}
          </span>
          <button
            type="button"
            className="btn-close btn-close-sm"
            style={{ transform: "scale(0.7)" }}
            aria-label="Remove commit hash"
            onClick={() => removeAt(idx)}
          />
        </span>
      ))}
      <div className="employee-github-link-tags-entry">
        <input
          id={inputId}
          type="text"
          className="employee-github-link-tags-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addFromDraft();
            }
          }}
          placeholder="Commit hash — Enter or + Add hash"
          autoComplete="off"
          spellCheck={false}
        />
        {draft.trim() ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary employee-github-link-tags-add-btn"
            aria-label="Add hash from field"
            onClick={() => addFromDraft()}
          >
            + Add hash
          </button>
        ) : null}
      </div>
    </div>
  );
}

function SubtaskCommitHashesAccordion({
  accordionDomId,
  parentRowForLinks,
  hashesPerRepo,
  onRepoHashesChange,
}: {
  accordionDomId: string;
  parentRowForLinks: unknown;
  hashesPerRepo: string[][];
  onRepoHashesChange: (repoIndex: number, hashes: string[]) => void;
}) {
  const urls = githubLinksFromRow(parentRowForLinks);
  if (!urls.length) {
    return (
      <p className="text-muted small mb-0">
        No GitHub links on this job. Add repository URLs on the job record to
        enter commit hashes per repository.
      </p>
    );
  }
  return (
    <div className="accordion" id={accordionDomId}>
      {urls.map((url, i) => {
        const headingId = `${accordionDomId}-h-${i}`;
        const collapseId = `${accordionDomId}-c-${i}`;
        const stripped = url.replace(/^https?:\/\//i, "");
        const short =
          stripped.length > 52 ? `${stripped.slice(0, 52)}…` : stripped;
        return (
          <div className="accordion-item" key={`${i}-${url.slice(0, 40)}`}>
            <h2 className="accordion-header" id={headingId}>
              <button
                className="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target={`#${collapseId}`}
                aria-expanded="false"
                aria-controls={collapseId}
              >
                <span className="text-truncate me-2" title={url}>
                  Repo {i + 1}: {short || url}
                </span>
              </button>
            </h2>
            <div
              id={collapseId}
              className="accordion-collapse collapse"
              data-bs-parent={`#${accordionDomId}`}
              aria-labelledby={headingId}
            >
              <div className="accordion-body">
                <p
                  className="form-label small text-muted mb-2"
                  id={`${collapseId}-hash-label`}
                >
                  Commit hashes for this repository
                </p>
                <SubtaskCommitHashTagsField
                  idPrefix={`${collapseId}-hash-tags`}
                  labelId={`${collapseId}-hash-label`}
                  hashes={hashesPerRepo[i] ?? []}
                  onChange={(next) => onRepoHashesChange(i, next)}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SubjobAddModalForm({
  subTask,
  parentJobRow,
  handleInputHours,
  handleSubtaskCreatedDateChange,
  setAddSubtaskRepoHashes,
}: {
  subTask: {
    subTaskName: string;
    subTaskDesc: string;
    subTaskTimeHrs: string;
    subTaskTimeMin: string;
    subTaskCreatedAt: Date;
    developerName: string;
    verificationEndpointsText: string;
    commitHashesByRepo: string[][];
  };
  parentJobRow: unknown;
  handleInputHours: (e: any) => void;
  handleSubtaskCreatedDateChange: (date: Date | null) => void;
  setAddSubtaskRepoHashes: (repoIndex: number, hashes: string[]) => void;
}) {
  return (
    <>
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label" htmlFor="add-subtask-name">
            SubJob name
          </label>
          <input
            type="text"
            className="form-control"
            id="add-subtask-name"
            name="subTaskName"
            value={subTask?.subTaskName}
            onChange={handleInputHours}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor="add-subtask-dev">
            Developer name
          </label>
          <input
            type="text"
            className="form-control"
            id="add-subtask-dev"
            name="developerName"
            value={subTask.developerName}
            onChange={handleInputHours}
            autoComplete="off"
          />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor="add-subtask-desc">
            Description
          </label>
          <input
            type="text"
            className="form-control"
            id="add-subtask-desc"
            name="subTaskDesc"
            value={subTask.subTaskDesc}
            onChange={handleInputHours}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor="add-subtask-created">
            Created date
          </label>
          <DatePicker
            id="add-subtask-created"
            className="form-control w-100"
            selected={subTask.subTaskCreatedAt}
            onChange={handleSubtaskCreatedDateChange}
            dateFormat="dd-MM-yyyy"
            placeholderText="dd-MM-yyyy"
            locale="en-AU"
          />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor="add-subtask-hrs">
            Estimated time (hours)
          </label>
          <input
            min={0}
            type="number"
            className="form-control"
            id="add-subtask-hrs"
            name="subTaskTimeHrs"
            value={subTask.subTaskTimeHrs}
            onChange={handleInputHours}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor="add-subtask-min">
            Estimated time (minutes)
          </label>
          <input
            min={0}
            type="number"
            className="form-control"
            id="add-subtask-min"
            name="subTaskTimeMin"
            value={subTask.subTaskTimeMin}
            onChange={handleInputHours}
          />
        </div>
      </div>
      <div className="row g-3 mt-1">
        <div className="col-12">
          <label
            className="form-label"
            htmlFor="add-subtask-verification-endpoints"
          >
            Verification endpoints
          </label>
          <textarea
            className="form-control"
            id="add-subtask-verification-endpoints"
            name="verificationEndpointsText"
            rows={3}
            value={subTask.verificationEndpointsText}
            onChange={handleInputHours}
            placeholder="One URL per line"
          />
        </div>
        <div className="col-12">
          <label className="form-label">Commit hashes by job repository</label>
          <SubtaskCommitHashesAccordion
            accordionDomId="addSubtaskCommitAccordion"
            parentRowForLinks={parentJobRow}
            hashesPerRepo={subTask.commitHashesByRepo}
            onRepoHashesChange={setAddSubtaskRepoHashes}
          />
        </div>
      </div>
    </>
  );
}

function SubjobEditModalForm({
  accordionDomId,
  formIdSuffix = "",
  editSubtask,
  parentRowForGithub,
  handleInputHours,
  setEditSubtaskRepoHashes,
}: {
  accordionDomId: string;
  formIdSuffix?: string;
  editSubtask: {
    name: string;
    taskDescriptions: string;
    estimatedtimeHrs: string | number;
    estimatedtimeMin: string | number;
    developerName: string;
    verificationEndpointsText: string;
    commitHashesByRepo: string[][];
  };
  parentRowForGithub: unknown;
  handleInputHours: (e: any) => void;
  setEditSubtaskRepoHashes: (repoIndex: number, hashes: string[]) => void;
}) {
  return (
    <>
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label" htmlFor={`edit-subtask-name${formIdSuffix}`}>
            SubJob name
          </label>
          <input
            type="text"
            className="form-control"
            id={`edit-subtask-name${formIdSuffix}`}
            name="name"
            value={editSubtask?.name}
            onChange={handleInputHours}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor={`edit-subtask-dev${formIdSuffix}`}>
            Developer name
          </label>
          <input
            type="text"
            className="form-control"
            id={`edit-subtask-dev${formIdSuffix}`}
            name="developerName"
            value={editSubtask.developerName}
            onChange={handleInputHours}
            autoComplete="off"
          />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor={`edit-subtask-desc${formIdSuffix}`}>
            Description
          </label>
          <input
            type="text"
            className="form-control"
            id={`edit-subtask-desc${formIdSuffix}`}
            name="taskDescriptions"
            value={editSubtask.taskDescriptions}
            onChange={handleInputHours}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor={`edit-subtask-hrs${formIdSuffix}`}>
            Estimated time (hours)
          </label>
          <input
            min={0}
            type="number"
            className="form-control"
            id={`edit-subtask-hrs${formIdSuffix}`}
            name="estimatedtimeHrs"
            value={editSubtask.estimatedtimeHrs}
            onChange={handleInputHours}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor={`edit-subtask-min${formIdSuffix}`}>
            Estimated time (minutes)
          </label>
          <input
            min={0}
            type="number"
            className="form-control"
            id={`edit-subtask-min${formIdSuffix}`}
            name="estimatedtimeMin"
            value={editSubtask.estimatedtimeMin}
            onChange={handleInputHours}
          />
        </div>
      </div>
      <div className="row g-3 mt-1">
        <div className="col-12">
          <label
            className="form-label"
            htmlFor={`edit-subtask-verification-endpoints${formIdSuffix}`}
          >
            Verification endpoints
          </label>
          <textarea
            className="form-control"
            id={`edit-subtask-verification-endpoints${formIdSuffix}`}
            name="verificationEndpointsText"
            rows={3}
            value={editSubtask.verificationEndpointsText}
            onChange={handleInputHours}
            placeholder="One URL per line"
          />
        </div>
        <div className="col-12">
          <label className="form-label">Commit hashes by job repository</label>
          <SubtaskCommitHashesAccordion
            accordionDomId={accordionDomId}
            parentRowForLinks={parentRowForGithub}
            hashesPerRepo={editSubtask.commitHashesByRepo}
            onRepoHashesChange={setEditSubtaskRepoHashes}
          />
        </div>
      </div>
    </>
  );
}

const Employee = () => {
  const { user } = useAuth();
  const employeeTabsRef = useRef<EmployeeTabsHandle>(null);
  const [task, setTask] = useState<any>();
  const [formData, setFormData] = useState({
    clientGroup: "",
    projectId: "",
    timeHrs: "",
    timeMin: "",
    projectDesc: "",
    activityType: "",
    details: "",
    notes: "",
    teamMember: "",
    githubLink: "",
    endDate: new Date(),
    startDate: new Date(), // Add startDate
    periodType: "", // Add this field
    financialYear: "", // Add this field
    financialPeriod: "", // Add this field
    periodStartDate: new Date(),
    periodEndDate: new Date(),
    instantDate: new Date(), // Add instantDate
  });

  const [report, setReport] = useState<any>();
  const [reportGenerating, setReportGenerating] = useState(false);
  const [editId, setEditId] = useState<any>();
  const [selectedLog, setSelectedLog] = useState<{
    projectId: string;
    logId: string;
    date: Date;
    timeHrsApplied: string;
    timeMinsApplied: string;
    notes: string;
    status?: unknown;
    statusBoolean?: unknown;
  }>({
    projectId: "",
    logId: "",
    date: new Date(),
    timeHrsApplied: "",
    timeMinsApplied: "",
    notes: "",
  });
  const [AIDescLoading, setAIDescLoading] = useState(false);
  // const [filteredTasks, setFilteredTasks] = useState([]);
  const DANIYAL = "daniyal";
  const editModalCloseRef = useRef<any>();
  const addModalCloseRef = useRef<any>();
  const logHoursModalCloseRef = useRef<any>();
  const editLogHoursModalCloseRef = useRef<any>();
  const subtaskModalRef = useRef<any>();
  const editSubtaskModalCloseRef = useRef<any>();
  /** Parent job row (from Jobs list) when editing a sub job — used for GitHub link accordions. */
  const [editSubtaskParentJob, setEditSubtaskParentJob] = useState<any>(null);
  /** Which step to reopen after closing the report result modal (`#weeklyReport`). */
  const weeklyReportReturnTargetRef = useRef<"date" | "period" | null>(null);
  // Handle input change
  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleEdit = async (e: any) => {
    e.preventDefault();

    const jobRangeErr = getDateRangeOrderError(
      formData.startDate,
      formData.endDate,
    );
    if (jobRangeErr) {
      toast.error(jobRangeErr);
      return;
    }
    if (formData.periodType === "durationPeriod") {
      const periodErr = getDateRangeOrderError(
        formData.periodStartDate,
        formData.periodEndDate,
      );
      if (periodErr) {
        toast.error(periodErr);
        return;
      }
    }

    // Helper function to safely format dates
    const safeFormatDate = (dateValue: any) => {
      if (!dateValue) return "";
      try {
        const date =
          dateValue instanceof Date ? dateValue : new Date(dateValue);
        if (isNaN(date.getTime())) {
          console.warn("Invalid date value:", dateValue);
          return format(new Date(), "dd-MM-yyyy");
        }
        return format(date, "dd-MM-yyyy");
      } catch (error) {
        console.warn("Error formatting date:", dateValue, error);
        return format(new Date(), "dd-MM-yyyy");
      }
    };

    const formattedEndDate = safeFormatDate(formData.endDate);
    const periodStartDate = safeFormatDate(formData.periodStartDate);
    const periodEndDate = safeFormatDate(formData.periodEndDate);
    const instantDate = safeFormatDate(formData.instantDate);

    const githubLink = serializeGithubLinkForApi(formData.githubLink);

    const taskData = {
      ...formData,
      endDate: formattedEndDate, // Set formatted date
      periodStartDate:
        formData.periodType === "durationPeriod" ? periodStartDate : "",
      periodEndDate:
        formData.periodType === "durationPeriod" ? periodEndDate : "",
      instantDate: formData.periodType === "instant" ? instantDate : "",
      githubLink,
    };

    try {
      console.log("Edit Id in handleEdit: ", editId);
      console.log("task data before API call: ", taskData);

      await editTask(editId, taskData);

      employeeTabsRef.current?.refetchJobs();
      employeeTabsRef.current?.refetchJobLogs();

      // Reset the form data
      setFormData({
        clientGroup: "",
        projectId: "",
        timeHrs: "",
        timeMin: "",
        projectDesc: "",
        activityType: "",
        details: "",
        notes: "",
        teamMember: "",
        githubLink: "",
        endDate: new Date(),
        startDate: new Date(),
        periodType: "",
        periodStartDate: new Date(),
        periodEndDate: new Date(),
        instantDate: new Date(),
        financialYear: "",
        financialPeriod: "",
      });
    } catch (error) {
      console.error("Error editing task:", error);
    } finally {
      setEditId(null);
      editModalCloseRef.current?.click();
    }
  };

  // Handle form submission
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const jobRangeErr = getDateRangeOrderError(
      formData.startDate,
      formData.endDate,
    );
    if (jobRangeErr) {
      toast.error(jobRangeErr);
      return;
    }
    if (formData.periodType === "durationPeriod") {
      const periodErr = getDateRangeOrderError(
        formData.periodStartDate,
        formData.periodEndDate,
      );
      if (periodErr) {
        toast.error(periodErr);
        return;
      }
    }
    const formattedEndDate = format(formData.endDate, "dd-MM-yyyy");
    const formattedStartDate = format(formData.startDate, "dd-MM-yyyy");
    const formattedPeriodStartDate =
      formData.periodStartDate &&
      format(formData.periodStartDate!, "dd-MM-yyyy");
    const formattedPeriodEndDate =
      formData.periodEndDate && format(formData.periodEndDate!, "dd-MM-yyyy");
    const formattedInstantDate =
      formData.instantDate && format(formData.instantDate!, "dd-MM-yyyy");

    const githubLink = serializeGithubLinkForApi(formData.githubLink);

    const taskData = {
      ...formData,
      userId: user?.user_id,
      endDate: formattedEndDate, // Set formatted date
      startDate: formattedStartDate,
      periodStartDate:
        formData.periodType === "durationPeriod"
          ? formattedPeriodStartDate
          : "",
      periodEndDate:
        formData.periodType === "durationPeriod" ? formattedPeriodEndDate : "",
      instantDate:
        formData.periodType === "instant" ? formattedInstantDate : "",
      githubLink,
    };

    try {
      await addTasks(taskData);
      employeeTabsRef.current?.refetchJobsAfterAdd();
      setFormData({
        clientGroup: "",
        projectId: "",
        timeHrs: "",
        timeMin: "",
        projectDesc: "",
        activityType: "",
        details: "",
        notes: "",
        teamMember: "",
        githubLink: "",
        endDate: new Date(),
        startDate: new Date(),
        periodStartDate: new Date(),
        periodEndDate: new Date(),
        instantDate: new Date(),
        periodType: "",
        financialPeriod: "",
        financialYear: "",
      });
    } catch (e) {
      console.log(e);
    } finally {
      addModalCloseRef.current?.click();
    }
    // You can handle the form submission logic here (e.g., send data to a server)
  };

  const [hoursLogged, setHoursLogged] = useState({
    taskId: "",
    subtask_id: "",
    date: new Date(),
    timeHrsApplied: "",
    timeMinsApplied: "",
    details: "",
    logNotes: "",
  });

  const [subTask, setSubtask] = useState({
    parentTaskId: "",
    subTaskName: "",
    subTaskDesc: "",
    subTaskTimeHrs: "",
    subTaskTimeMin: "",
    subTaskCreatedAt: new Date(),
    developerName: "",
    verificationEndpointsText: "",
    commitHashesByRepo: [] as string[][],
  });

  const [editSubtask, setEditSubtask] = useState({
    subtask_id: "",
    name: "",
    taskDescriptions: "",
    estimatedtimeHrs: "",
    estimatedtimeMin: "",
    developerName: "",
    verificationEndpointsText: "",
    commitHashesByRepo: [] as string[][],
  });

  useEffect(() => {
    const links = githubLinksFromRow(task ?? {});
    setSubtask((prev) => ({
      ...prev,
      commitHashesByRepo: links.map(
        (_, i) => prev.commitHashesByRepo[i] ?? [],
      ),
    }));
  }, [task?.taskId]);

  const helpDeskOptions = ["Calls", "Tickets", "Admin", "Contents"];
  const complianceOptions = [
    "Bookkeeping",
    "Activity S",
    "Annual C",
    "SMSF",
    "ASIC",
    "Admin",
  ];

  // Handle input changes
  const handleInputHours = (e: any) => {
    const { name, value } = e.target;

    console.log(value);
    setHoursLogged((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setSubtask((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setEditSubtask((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setSelectedLog((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const setAddSubtaskRepoHashes = (repoIndex: number, hashes: string[]) => {
    setSubtask((prev) => {
      const links = githubLinksFromRow(task ?? {});
      const len = Math.max(links.length, repoIndex + 1, prev.commitHashesByRepo.length);
      const next = Array.from({ length: len }, (_, i) =>
        i === repoIndex ? hashes : [...(prev.commitHashesByRepo[i] ?? [])],
      );
      return { ...prev, commitHashesByRepo: next };
    });
  };

  const setEditSubtaskRepoHashes = (repoIndex: number, hashes: string[]) => {
    setEditSubtask((prev) => {
      const parent = editSubtaskParentJob ?? prev;
      const linkCount = githubLinksFromRow(parent).length;
      const len = Math.max(linkCount, repoIndex + 1, prev.commitHashesByRepo.length);
      const next = Array.from({ length: len }, (_, i) =>
        i === repoIndex ? hashes : [...(prev.commitHashesByRepo[i] ?? [])],
      );
      return { ...prev, commitHashesByRepo: next };
    });
  };

  const handleSubtaskCreatedDateChange = (date: Date | null) => {
    setSubtask((prev) => ({
      ...prev,
      subTaskCreatedAt: date || new Date(),
    }));
  };

  const handleDateChange = (date: any) => {
    setFormData((prevData: any) => ({
      ...prevData,
      endDate: date, // Set the date directly
    }));
  };

  const handleLoggedDateChange = (date: any) => {
    setHoursLogged((prevData: any) => ({
      ...prevData,
      date: date, // Set the date directly
    }));
  };
  // Handle form submission
  const handleHoursSubmit = async (e: any) => {
    e.preventDefault();

    // var today = new Date();
    // var dd = String(today.getDate()).padStart(2, '0');
    // var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
    // var yyyy = today.getFullYear();

    // hoursLogged.date = mm + '/' + dd + '/' + yyyy;

    const formattedDate = format(hoursLogged.date, "dd-MM-yyyy");

    const taskData = {
      ...hoursLogged,
      taskId: task.taskId,
      date: formattedDate,
      userId: user?.user_id,

      // Set formatted date
    };

    try {
      await logHours(taskData);
      employeeTabsRef.current?.refetchJobLogs();
      setHoursLogged({
        taskId: "",
        subtask_id: "",
        date: new Date(),
        timeHrsApplied: "",
        timeMinsApplied: "",
        details: "",
        logNotes: "",
      });
    } catch (e) {
    } finally {
      logHoursModalCloseRef.current?.click();
    }

    // const data = new FormData();
    // for (const [key, value] of Object.entries(formData)) {
    //     data.append(key, value);
    // }

    // Process the FormData (you can send it to an API or handle as needed)
    console.log("Form Data Submitted:", hoursLogged);
  };

  const handleSubTaskHoursSubmit = async (e: any) => {
    e.preventDefault();

    // var today = new Date();
    // var dd = String(today.getDate()).padStart(2, '0');
    // var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
    // var yyyy = today.getFullYear();

    // hoursLogged.date = mm + '/' + dd + '/' + yyyy;

    const formattedDate = format(hoursLogged.date, "dd-MM-yyyy");

    const taskData = {
      subtaskId: task.subtaskId,
      date: formattedDate,
      userId: user?.user_id,
      timeHrsApplied: hoursLogged.timeHrsApplied,
      timeMinsApplied: +hoursLogged.timeMinsApplied,
      notes: hoursLogged.logNotes,
      // Set formatted date
    };

    try {
      await addSubtaskLog(taskData);
      setHoursLogged({
        taskId: "",
        subtask_id: "",
        date: new Date(),
        timeHrsApplied: "",
        timeMinsApplied: "",
        details: "",
        logNotes: "",
      });
      employeeTabsRef.current?.refetchSubtaskLogs();
    } catch (e) {
    } finally {
      // logHoursModalCloseRef.current?.click();
    }

    // const data = new FormData();
    // for (const [key, value] of Object.entries(formData)) {
    //     data.append(key, value);
    // }

    // Process the FormData (you can send it to an API or handle as needed)
    console.log("Form Data Submitted:", hoursLogged);
  };

  const handleSubtaskSubmit = async (e: any) => {
    e.preventDefault();

    // var today = new Date();
    // var dd = String(today.getDate()).padStart(2, '0');
    // var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
    // var yyyy = today.getFullYear();

    // hoursLogged.date = mm + '/' + dd + '/' + yyyy;

    const formattedDate = format(subTask.subTaskCreatedAt, "dd-MM-yyyy");
    if (!task?.taskId) return;
    const parentLinks = githubLinksFromRow(task ?? {});
    const verificationEndpoints = nonEmptyVerificationList(
      subTask.verificationEndpointsText,
    );
    const commitHashes = buildCommitHashesPayload(
      parentLinks,
      subTask.commitHashesByRepo,
    );
    const extras = buildSubtaskExtrasBody(
      verificationEndpoints,
      commitHashes,
    );

    try {
      await addSubtask(
        {
          taskId: Number(task.taskId),
          userId: user?.user_id,
          name: subTask.subTaskName,
          taskDescriptions: subTask.subTaskDesc,
          estimatedtimeHrs: Number(subTask.subTaskTimeHrs) || 0,
          estimatedtimeMin: Number(subTask.subTaskTimeMin) || 0,
          createdDate: formattedDate,
          status: false,
          developerName: (subTask.developerName ?? "").trim() || null,
        },
        extras,
      );
      employeeTabsRef.current?.refetchSubJobs();
      setSubtask({
        parentTaskId: "",
        subTaskName: "",
        subTaskDesc: "",
        subTaskTimeHrs: "",
        subTaskTimeMin: "",
        subTaskCreatedAt: new Date(),
        developerName: "",
        verificationEndpointsText: "",
        commitHashesByRepo: parentLinks.map(() => []),
      });
    } catch (e) {
    } finally {
      subtaskModalRef?.current?.click();
    }

    // const data = new FormData();
    // for (const [key, value] of Object.entries(formData)) {
    //     data.append(key, value);
    // }

    // Process the FormData (you can send it to an API or handle as needed)
    console.log("Form Data Submitted:", hoursLogged);
  };

  const handleEditClick = (task: any) => {
    console.log("Task received:", task);

    // Helper function to parse date strings to Date objects
    const parseDate = (dateValue: any) => {
      if (!dateValue || dateValue === "") {
        return new Date();
      }
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? new Date() : parsed;
    };

    const formData = {
      clientGroup: task.clientGroup || "",
      projectId: task.projectId || "",
      timeHrs: task.timeHrs || 0,
      timeMin: task.timeMin || 0, // Changed from '' to 0 since it's a number
      projectDesc: task.projectDesc || "",
      activityType: task.activityType || "",
      details: task.details || "",
      notes: task.notes || "",
      teamMember: task.teamMembers || "", // Note: teamMembers in data but teamMember in form
      githubLink: taskGithubLinkToFormField(task),
      endDate: parseDate(task.endDate),
      startDate: parseDate(task.startDate),
      periodStartDate: parseDate(task.periodStartDate),
      periodEndDate: parseDate(task.periodEndDate),
      instantDate: parseDate(task.instantDate),
      periodType: task.periodType || "",
      financialPeriod: task.financialPeriod || "",
      financialYear: task.financialYear || "",
      userId: task.userId || "",
    };

    console.log("Setting form data:", formData);
    setFormData(formData);
    setEditId(task.taskId);
  };

  const handleSubTaskEditClick = (taskRow: any, parentJob?: any) => {
    setEditId(taskRow.taskId);
    setEditSubtaskParentJob(parentJob ?? null);
    const parent = parentJob ?? taskRow;
    const parentLinks = githubLinksFromRow(parent);
    setEditSubtask({
      subtask_id: taskRow.subtaskId,
      name: taskRow.name ?? "",
      taskDescriptions: taskRow.taskDescriptions ?? "",
      estimatedtimeHrs: taskRow.estimatedtimeHrs ?? "",
      estimatedtimeMin: taskRow.estimatedtimeMin ?? "",
      developerName: taskRow.developerName ?? "",
      verificationEndpointsText: verificationEndpointsToText(
        taskRow.verificationEndpoints,
      ),
      commitHashesByRepo: commitHashesToDraftEntries(
        taskRow.commitHashes,
        parentLinks,
      ),
    });
  };

  useEffect(() => {
    const modal = document.getElementById("exampleModalToggle2");
    if (modal) {
      modal.addEventListener("hidden.bs.modal", () => {
        setFormData({
          clientGroup: "",
          projectId: "",
          timeHrs: "",
          timeMin: "",
          projectDesc: "",
          activityType: "",
          details: "",
          notes: "",
          teamMember: "",
          githubLink: "",
          endDate: new Date(),
          startDate: new Date(),
          financialPeriod: "",
          financialYear: "",
          periodStartDate: new Date(),
          periodEndDate: new Date(),
          instantDate: new Date(),
          periodType: "",
        });
        setEditId(null);
      });
    }
  }, []);

  const generateWeeklyReport = async () => {
    weeklyReportReturnTargetRef.current = null;
    setReportGenerating(true);
    setReport("");
    try {
      const response = await generateReport({ userId: user?.user_id });

      // Assuming response.data is an array of strings
      if (Array.isArray(response?.data)) {
        // Join the array of strings with a new line and set it to the report
        setReport(response.data.join("\n"));
      } else {
        console.error("Response data is not an array");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setReportGenerating(false);
    }
  };

  const handleDeleteClick = async (task: any) => {
    // try {
    //   await deleteTask(task.taskId);
    //   setTasks((prevTasks: any) =>
    //     prevTasks.filter((t: any) => t.taskId !== task.taskId)
    //   );
    //   setTasks1((prevTasks1: any) =>
    //     prevTasks1.logs.filter((t: any) => t.taskId !== task.taskId)
    //   );
    // } catch (e) {
    //   console.log(e);
    // }
  };

  // Add handler for start date changes
  const handleStartDateChange = (date: any) => {
    setFormData((prevData: any) => ({
      ...prevData,
      startDate: date,
    }));
  };
  const handlePeriodStartDateChange = (date: any) => {
    setFormData((prevData: any) => ({
      ...prevData,
      periodStartDate: date,
    }));
  };
  const handlePeriodEndDateChange = (date: any) => {
    setFormData((prevData: any) => ({
      ...prevData,
      periodEndDate: date, // Set the date directly
    }));
  };

  const handleInstantDateChange = (date: any) => {
    setFormData((prevData: any) => ({
      ...prevData,
      instantDate: date, // Set the date directly
    }));
  };

  // Update the weekly report modal form submission
  const handleWeeklyReportSubmit = async (e: any) => {
    e.preventDefault();
    const reportRangeErr = getDateRangeOrderError(
      formData.startDate,
      formData.endDate,
    );
    if (reportRangeErr) {
      toast.error(reportRangeErr);
      return;
    }
    if (formData.periodType === "durationPeriod") {
      const periodErr = getDateRangeOrderError(
        formData.periodStartDate,
        formData.periodEndDate,
      );
      if (periodErr) {
        toast.error(periodErr);
        return;
      }
    }
    weeklyReportReturnTargetRef.current =
      formData.periodType === "financialTimeFrame" ? "period" : "date";
    setReportGenerating(true);
    setReport("");
    const formattedStartDate = format(formData.startDate, "dd-MM-yyyy");
    const formattedEndDate = format(formData.endDate, "dd-MM-yyyy");

    let reportParams: any = {
      userId: user?.user_id,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      periodType: formData.periodType,
    };

    if (formData.periodType === "durationPeriod") {
      reportParams = {
        ...reportParams,
        periodStartDate: format(formData.periodStartDate!, "dd-MM-yyyy"),
        periodEndDate: format(formData.periodEndDate!, "dd-MM-yyyy"),
        financialPeriod: null,
        financialYear: null,
        instantDate: null,
      };
    } else if (formData.periodType === "financialTimeFrame") {
      reportParams = {
        ...reportParams,
        periodStartDate: null,
        periodEndDate: null,
        financialPeriod: formData.financialPeriod,
        financialYear: formData.financialYear,
        instantDate: null,
      };
    } else if (formData.periodType === "instant") {
      reportParams = {
        ...reportParams,
        periodStartDate: null,
        periodEndDate: null,
        financialPeriod: null,
        financialYear: null,
        instantDate: format(formData.instantDate!, "dd-MM-yyyy"),
      };
    }

    try {
      const response = await generateReport(reportParams);

      if (Array.isArray(response?.data)) {
        setReport(response.data.join("\n"));
        setFormData({
          clientGroup: "",
          projectId: "",
          timeHrs: "",
          timeMin: "",
          projectDesc: "",
          activityType: "",
          details: "",
          notes: "",
          teamMember: "",
          githubLink: "",
          endDate: new Date(),
          startDate: new Date(),
          financialPeriod: "",
          financialYear: "",
          periodStartDate: new Date(),
          periodEndDate: new Date(),
          instantDate: new Date(),
          periodType: "",
        });
      } else {
        console.error("Response data is not an array");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setReportGenerating(false);
    }
  };

  const handleSubTaskEdit = async (e: any) => {
    e.preventDefault();
    const parentLinks = githubLinksFromRow(
      editSubtaskParentJob ?? editSubtask,
    );
    const verificationEndpoints = nonEmptyVerificationList(
      editSubtask.verificationEndpointsText,
    );
    const commitHashes = buildCommitHashesPayload(
      parentLinks,
      editSubtask.commitHashesByRepo,
    );
    const extras = buildSubtaskExtrasBody(
      verificationEndpoints,
      commitHashes,
    );
    try {
      await updateSubTask(
        {
          userId: user?.user_id,
          subtask_id: Number(editSubtask.subtask_id),
          name: editSubtask.name,
          taskDescriptions: editSubtask.taskDescriptions,
          estimatedtimeHrs: Number(editSubtask.estimatedtimeHrs) || 0,
          estimatedtimeMin: Number(editSubtask.estimatedtimeMin) || 0,
          developerName: (editSubtask.developerName ?? "").trim() || null,
        },
        extras,
      );
      employeeTabsRef.current?.refetchSubJobs();
    } finally {
      editSubtaskModalCloseRef?.current?.click();
    }
  };

  // Weekly report flow: choice → date | period → result. Bootstrap stacked modals
  // can leave extra .modal-backdrop / modal-open on <body>. Normalize on close.
  useEffect(() => {
    const clearBodyModalArtifactsIfIdle = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (document.querySelector(".modal.show")) return;
          document
            .querySelectorAll("body > .modal-backdrop")
            .forEach((el) => el.remove());
          document.body.classList.remove("modal-open");
          document.body.style.removeProperty("padding-right");
          document.body.style.removeProperty("overflow");
        });
      });
    };

    const reopenChoiceIfNeeded = () => {
      window.setTimeout(() => {
        const reportEl = document.getElementById("weeklyReport");
        const choiceEl = document.getElementById("weeklyReportChoice");
        if (!choiceEl || reportEl?.classList.contains("show")) return;

        document
          .querySelectorAll("body > .modal-backdrop")
          .forEach((el) => el.remove());
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");

        Modal.getOrCreateInstance(choiceEl).show();
      }, 100);
    };

    const resetPeriodFields = () => {
      setFormData((prevData) => ({
        ...prevData,
        periodType: "",
        financialYear: "",
        financialPeriod: "",
        periodStartDate: new Date(),
        periodEndDate: new Date(),
        instantDate: new Date(),
      }));
    };

    const dateEl = document.getElementById("weeklyReportDateModal");
    const periodEl = document.getElementById("weeklyReportPeriodModal");
    const choiceEl = document.getElementById("weeklyReportChoice");
    const reportEl = document.getElementById("weeklyReport");

    const onDateHidden = () => reopenChoiceIfNeeded();
    const onPeriodHidden = () => {
      resetPeriodFields();
      reopenChoiceIfNeeded();
    };
    const onChoiceHidden = () => {
      // Choice also hides when opening By Date / By Period — do not clear then.
      window.setTimeout(() => {
        const dateOpen =
          document.getElementById("weeklyReportDateModal")?.classList.contains("show");
        const periodOpen =
          document.getElementById("weeklyReportPeriodModal")?.classList.contains("show");
        if (dateOpen || periodOpen) return;
        setReport("");
        setReportGenerating(false);
        clearBodyModalArtifactsIfIdle();
      }, 150);
    };
    const onReportHidden = () => {
      setReport("");
      setReportGenerating(false);
      const returnTo = weeklyReportReturnTargetRef.current;
      weeklyReportReturnTargetRef.current = null;
      window.setTimeout(() => {
        if (returnTo === "date") {
          const el = document.getElementById("weeklyReportDateModal");
          if (el) Modal.getOrCreateInstance(el).show();
          return;
        }
        if (returnTo === "period") {
          const el = document.getElementById("weeklyReportPeriodModal");
          if (el) Modal.getOrCreateInstance(el).show();
          return;
        }
        clearBodyModalArtifactsIfIdle();
      }, 150);
    };

    dateEl?.addEventListener("hidden.bs.modal", onDateHidden);
    periodEl?.addEventListener("hidden.bs.modal", onPeriodHidden);
    choiceEl?.addEventListener("hidden.bs.modal", onChoiceHidden);
    reportEl?.addEventListener("hidden.bs.modal", onReportHidden);

    return () => {
      dateEl?.removeEventListener("hidden.bs.modal", onDateHidden);
      periodEl?.removeEventListener("hidden.bs.modal", onPeriodHidden);
      choiceEl?.removeEventListener("hidden.bs.modal", onChoiceHidden);
      reportEl?.removeEventListener("hidden.bs.modal", onReportHidden);
    };
  }, []);
  const handleEditLogHours = async (e: any) => {
    e.preventDefault();
    const taskData = {
      log_id: Number(selectedLog.logId),
      date: format(selectedLog.date, "dd-MM-yyyy"),
      timeHrsApplied: selectedLog.timeHrsApplied,
      timeMinsApplied: selectedLog.timeMinsApplied,
      notes: selectedLog.notes,
    };
    try {
      await editTasklog(taskData);
      employeeTabsRef.current?.refetchJobLogs();
    } catch (error) {
    } finally {
      editLogHoursModalCloseRef?.current?.click();
    }
  };

  const generateAIDescription = async (e: any) => {
    e.preventDefault();
    setAIDescLoading(true);
    try {
      const response = await generateAIDesc(formData.projectDesc);
      console.log(response);

      // Remove "Enhanced Description:" if it exists
      const cleanedResponse = response?.data?.message.replace(
        /^Enhanced Description:\s*/,
        "",
      );

      setFormData((prevData) => ({
        ...prevData,
        projectDesc: cleanedResponse,
      }));
      // setFormData((prevData) => ({
      //   ...prevData,
      //   projectDesc: response?.data?.message
      // }));
      // const res2= await fetchTasks(1)
    } catch (error) {
    } finally {
      setAIDescLoading(false);
    }
  };

  // Add this handler after the other handlers for log editing
  const handleSelectedLogDateChange = (date: any) => {
    setSelectedLog((prevData: any) => ({
      ...prevData,
      date: date,
    }));
  };

  return (
    <div className="employee-panel">
      <div className="container-fluid">
        <div className="header d-flex justify-content-between">
          <h2>Employee Panel</h2>
          <button
            className="btn btn-danger"
            onClick={() => {
              localStorage.clear();
              window.location.href = "/";
            }}
          >
            Logout
          </button>{" "}
        </div>

        <div className=" mt-4">
          <EmployeeTabs
            ref={employeeTabsRef}
            modalTask={task}
            onSelectTask={setTask}
            onEditJob={handleEditClick}
            onEditSubjob={handleSubTaskEditClick}
            setSelectedLog={setSelectedLog}
          />

          <div className="card-header d-flex justify-content-between mb-4">
            <div className="heading">
              <h2></h2>
            </div>
            <div className="button">
              <div className="button">
                <button
                  type="button"
                  className="btn btn-primary"
                  data-bs-target="#weeklyReportChoice"
                  data-bs-toggle="modal"
                >
                  Generate Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add New Task Modal */}
      <div
        className="modal fade employee-task-modal"
        id="exampleModalToggle"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0 shadow employee-job-form-sheet">
            <div className="modal-header employee-job-form-modal-header py-3 px-4 align-items-center">
              <div className="me-3 flex-grow-1 min-w-0">
                <h1 className="modal-title h5 mb-0 fw-semibold" id="exampleModalToggleLabel">
                  Add new job
                </h1>
                <p className="employee-job-form-header-sub mb-0 mt-1">
                  Scope, timeline, and collaboration in one place.
                </p>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white flex-shrink-0"
                data-bs-dismiss="modal"
                aria-label="Close"
                ref={addModalCloseRef}
              ></button>
            </div>
            <div className="modal-body px-4 py-3">
              <form onSubmit={handleSubmit} className="employee-job-form">
                <div className="employee-job-form-section">
                  <div className="employee-job-form-section-title">
                    Job details
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="employee-add-clientGroup" className="form-label">
                        Client group
                      </label>
                      <input
                        type="text"
                        id="employee-add-clientGroup"
                        name="clientGroup"
                        className="form-control"
                        value={formData.clientGroup}
                        onChange={handleInputChange}
                        placeholder="Client or group name"
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="employee-add-projectId" className="form-label">
                        Job ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="employee-add-projectId"
                        name="projectId"
                        className="form-control"
                        value={formData.projectId}
                        onChange={handleInputChange}
                        placeholder="Unique job identifier"
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="employee-add-timeHrs" className="form-label">
                        Est. hours <span className="text-danger">*</span>
                      </label>
                      <input
                        min="0"
                        type="number"
                        id="employee-add-timeHrs"
                        name="timeHrs"
                        className="form-control"
                        placeholder="0"
                        required
                        value={formData.timeHrs}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="employee-add-timeMin" className="form-label">
                        Est. minutes <span className="text-danger">*</span>
                      </label>
                      <input
                        min="0"
                        max="59"
                        type="number"
                        id="employee-add-timeMin"
                        name="timeMin"
                        placeholder="0"
                        className="form-control"
                        required
                        value={formData.timeMin}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="employee-add-endDate" className="form-label">
                        End date
                      </label>
                      <DatePicker
                        id="employee-add-endDate"
                        name="endDate"
                        className="form-control w-100"
                        selected={formData.endDate}
                        onChange={handleDateChange}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="dd-MM-yyyy"
                        locale="en-AU"
                      />
                    </div>
                  </div>
                </div>

                <div className="employee-job-form-section">
                  <div className="employee-job-form-section-title">
                    Description &amp; collaboration
                  </div>
                  <div className="mb-3 employee-job-form-desc-wrap">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 employee-job-form-desc-toolbar">
                      <label
                        htmlFor="employee-add-projectDesc"
                        className="form-label mb-0 flex-grow-1 min-w-0"
                      >
                        Job description <span className="text-danger">*</span>
                      </label>
                      <button
                        className="btn btn-sm btn-primary employee-job-form-ai-btn flex-shrink-0"
                        type="button"
                        disabled={formData.projectDesc === "" || AIDescLoading}
                        onClick={generateAIDescription}
                      >
                        {AIDescLoading ? (
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        ) : null}
                        Generate AI description
                      </button>
                    </div>
                    <div className="employee-job-form-desc-inner">
                      <textarea
                        id="employee-add-projectDesc"
                        name="projectDesc"
                        rows={6}
                        className="form-control employee-job-form-textarea"
                        placeholder="What is this job about?"
                        value={formData.projectDesc}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="employee-add-teamMember" className="form-label">
                        Team members
                      </label>
                      <input
                        type="text"
                        id="employee-add-teamMember"
                        name="teamMember"
                        placeholder="Names, comma-separated"
                        className="form-control"
                        value={formData.teamMember}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label
                        id="employee-add-job-githubLink-label"
                        htmlFor="employee-add-job-githubLinkInput"
                        className="form-label"
                      >
                        GitHub links{" "}
                        <span className="text-muted fw-normal small">(optional)</span>
                      </label>
                      <GithubLinksTagField
                        idPrefix="employee-add-job"
                        labelId="employee-add-job-githubLink-label"
                        value={formData.githubLink}
                        onChange={(joined) =>
                          setFormData((prev: any) => ({
                            ...prev,
                            githubLink: joined,
                          }))
                        }
                      />
                      <div className="form-text">
                        Type or paste a URL, then press Enter or click &quot;+ Add
                        link&quot;. Multiple URLs in one paste (new lines or commas)
                        add several tags. Max 512 characters per link.
                      </div>
                    </div>
                  </div>
                {/* <div className="mb-3">
                  <label htmlFor="activityType">Activity Type:</label>
                  <select
                    id="activityType"
                    name="activityType"
                    value={formData.activityType}
                    onChange={handleInputHours}
                    //
                  >
                    <option value="">Select Activity Type</option>
                    <option value="helpDesk">Help Desk</option>
                    <option value="compliance">Compliance</option>
                  </select>
                </div>
                {formData.activityType && (
                  <div className="mb-3">
                    <label htmlFor="details">Details:</label>
                    <select
                      id="details"
                      name="details"
                      value={hoursLogged.details}
                      onChange={handleInputHours}
                    >
                      <option value="">Select Details</option>
                      {(formData.activityType === "helpDesk"
                        ? helpDeskOptions
                        : complianceOptions
                      ).map((option, index) => (
                        <option
                          key={index}
                          value={option.toLowerCase().replace(/\s+/g, "")}
                        >
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                )} */}
                  <div className="mb-0 mt-3">
                    <label htmlFor="employee-add-notes" className="form-label">
                      Notes
                    </label>
                    <textarea
                      id="employee-add-notes"
                      name="notes"
                      rows={3}
                      placeholder="Internal notes (optional)"
                      className="form-control"
                      value={formData.notes}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="employee-job-form-section">
                  <div className="employee-job-form-section-title">
                    Schedule &amp; period
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="employee-add-periodType" className="form-label">
                        Period type <span className="text-danger">*</span>
                      </label>
                      <select
                        id="employee-add-periodType"
                        name="periodType"
                        className="form-select"
                        value={formData.periodType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Type</option>
                        <option value="durationPeriod">Duration Period</option>
                        <option value="financialTimeFrame">
                          Financial Time Frame Association
                        </option>
                        <option value="instant">Instant</option>
                      </select>
                    </div>
                    <div className="col-12">
                      {formData.periodType === "instant" && (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label htmlFor="employee-add-instantDate" className="form-label">
                              Instant date
                            </label>
                            <DatePicker
                              id="employee-add-instantDate"
                              name="instantDate"
                              className="form-control w-100"
                              selected={formData.instantDate}
                              onChange={handleInstantDateChange}
                              dateFormat="dd-MM-yyyy"
                              placeholderText="dd-MM-yyyy"
                              locale="en-AU"
                            />
                          </div>
                        </div>
                      )}
                      {formData.periodType === "durationPeriod" && (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label htmlFor="employee-add-periodStartDate" className="form-label">
                              Period start
                            </label>
                            <DatePicker
                              id="employee-add-periodStartDate"
                              name="periodStartDate"
                              className="form-control w-100"
                              selected={formData.periodStartDate}
                              onChange={handlePeriodStartDateChange}
                              dateFormat="dd-MM-yyyy"
                              placeholderText="dd-MM-yyyy"
                              locale="en-AU"
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="employee-add-periodEndDate" className="form-label">
                              Period end
                            </label>
                            <DatePicker
                              id="employee-add-periodEndDate"
                              name="periodEndDate"
                              className="form-control w-100"
                              selected={formData.periodEndDate}
                              onChange={handlePeriodEndDateChange}
                              dateFormat="dd-MM-yyyy"
                              placeholderText="dd-MM-yyyy"
                              locale="en-AU"
                            />
                          </div>
                        </div>
                      )}
                      {formData.periodType === "financialTimeFrame" && (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <FinancialYearField
                              id="employee-add-financialYear"
                              label="Financial year"
                              value={formData.financialYear}
                              onChange={(financialYear) =>
                                setFormData((prev: any) => ({
                                  ...prev,
                                  financialYear,
                                }))
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="employee-add-financialPeriod" className="form-label">
                              Financial period
                            </label>
                            <select
                              id="employee-add-financialPeriod"
                              name="financialPeriod"
                              className="form-select"
                              value={formData.financialPeriod}
                              onChange={handleInputChange}
                            >
                              <option value="" disabled>
                                Select Financial Period
                              </option>
                              <option value="qtr1">Quarter 1</option>
                              <option value="qtr2">Quarter 2</option>
                              <option value="qtr3">Quarter 3</option>
                              <option value="qtr4">Quarter 4</option>
                              <option value="january">January</option>
                              <option value="february">February</option>
                              <option value="march">March</option>
                              <option value="april">April</option>
                              <option value="may">May</option>
                              <option value="june-july">{"July - June"}</option>
                              <option value="august">August</option>
                              <option value="september">September</option>
                              <option value="october">October</option>
                              <option value="november">November</option>
                              <option value="december">December</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-wrap justify-content-end gap-2 pt-3 mt-1 border-top employee-job-form-actions">
                  <button type="submit" className="btn btn-primary px-4">
                    Create job
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Task Modal */}
      <div
        className="modal fade employee-task-modal"
        id="exampleModalToggle2"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel2"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0 shadow employee-job-form-sheet">
            <div className="modal-header employee-job-form-modal-header py-3 px-4 align-items-center">
              <div className="me-3 flex-grow-1 min-w-0">
                <h1 className="modal-title h5 mb-0 fw-semibold" id="exampleModalToggleLabel2">
                  Edit job
                </h1>
                <p className="employee-job-form-header-sub mb-0 mt-1">
                  Update details; changes apply when you save.
                </p>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white flex-shrink-0"
                data-bs-dismiss="modal"
                aria-label="Close"
                ref={editModalCloseRef}
              ></button>
            </div>
            <div className="modal-body px-4 py-3">
              <form onSubmit={handleEdit} className="employee-job-form">
                <div className="employee-job-form-section">
                  <div className="employee-job-form-section-title">
                    Job details
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="employee-edit-clientGroup" className="form-label">
                        Client group
                      </label>
                      <input
                        type="text"
                        id="employee-edit-clientGroup"
                        name="clientGroup"
                        className="form-control"
                        value={formData.clientGroup}
                        onChange={handleInputChange}
                        placeholder="Client or group name"
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="employee-edit-projectId" className="form-label">
                        Job ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="employee-edit-projectId"
                        name="projectId"
                        className="form-control"
                        value={formData.projectId}
                        onChange={handleInputChange}
                        placeholder="Unique job identifier"
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="employee-edit-timeHrs" className="form-label">
                        Est. hours <span className="text-danger">*</span>
                      </label>
                      <input
                        min="0"
                        type="number"
                        id="employee-edit-timeHrs"
                        name="timeHrs"
                        className="form-control"
                        placeholder="0"
                        value={formData.timeHrs}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="employee-edit-timeMin" className="form-label">
                        Est. minutes <span className="text-danger">*</span>
                      </label>
                      <input
                        max="59"
                        min="0"
                        type="number"
                        id="employee-edit-timeMin"
                        name="timeMin"
                        placeholder="0"
                        className="form-control"
                        value={formData.timeMin}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="employee-edit-endDate" className="form-label">
                        End date
                      </label>
                      <DatePicker
                        id="employee-edit-endDate"
                        name="endDate"
                        className="form-control w-100"
                        selected={formData.endDate}
                        onChange={handleDateChange}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="dd-MM-yyyy"
                        locale="en-AU"
                      />
                    </div>
                  </div>
                </div>

                <div className="employee-job-form-section">
                  <div className="employee-job-form-section-title">
                    Description &amp; collaboration
                  </div>
                  <div className="mb-3 employee-job-form-desc-wrap">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 employee-job-form-desc-toolbar">
                      <label
                        htmlFor="employee-edit-projectDesc"
                        className="form-label mb-0 flex-grow-1 min-w-0"
                      >
                        Job description <span className="text-danger">*</span>
                      </label>
                      <button
                        className="btn btn-sm btn-primary employee-job-form-ai-btn flex-shrink-0"
                        type="button"
                        disabled={formData.projectDesc === "" || AIDescLoading}
                        onClick={generateAIDescription}
                      >
                        {AIDescLoading ? (
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        ) : null}
                        Generate AI description
                      </button>
                    </div>
                    <div className="employee-job-form-desc-inner">
                      <textarea
                        id="employee-edit-projectDesc"
                        name="projectDesc"
                        rows={6}
                        className="form-control employee-job-form-textarea"
                        placeholder="What is this job about?"
                        value={formData.projectDesc}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="employee-edit-teamMember" className="form-label">
                        Team members
                      </label>
                      <input
                        type="text"
                        id="employee-edit-teamMember"
                        name="teamMember"
                        placeholder="Names, comma-separated"
                        className="form-control"
                        value={formData.teamMember}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label
                        id="employee-edit-job-githubLink-label"
                        htmlFor="employee-edit-job-githubLinkInput"
                        className="form-label"
                      >
                        GitHub links{" "}
                        <span className="text-muted fw-normal small">(optional)</span>
                      </label>
                      <GithubLinksTagField
                        idPrefix="employee-edit-job"
                        labelId="employee-edit-job-githubLink-label"
                        value={formData.githubLink}
                        onChange={(joined) =>
                          setFormData((prev: any) => ({
                            ...prev,
                            githubLink: joined,
                          }))
                        }
                      />
                      <div className="form-text">
                        Type or paste a URL, then press Enter or click &quot;+ Add
                        link&quot;. Remove all tags and save to clear links on the
                        job.
                      </div>
                    </div>
                  </div>
                  <div className="mb-0 mt-3">
                    <label htmlFor="employee-edit-notes" className="form-label">
                      Notes
                    </label>
                    <textarea
                      id="employee-edit-notes"
                      name="notes"
                      rows={3}
                      placeholder="Internal notes (optional)"
                      className="form-control"
                      value={formData.notes}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="employee-job-form-section">
                  <div className="employee-job-form-section-title">
                    Schedule &amp; period
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="employee-edit-periodType" className="form-label">
                        Period type <span className="text-danger">*</span>
                      </label>
                      <select
                        id="employee-edit-periodType"
                        name="periodType"
                        className="form-select"
                        value={formData.periodType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Type</option>
                        <option value="durationPeriod">Duration Period</option>
                        <option value="financialTimeFrame">
                          Financial Time Frame Association
                        </option>
                        <option value="instant">Instant</option>
                      </select>
                    </div>
                    <div className="col-12">
                      {formData.periodType === "instant" && (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label htmlFor="employee-edit-instantDate" className="form-label">
                              Instant date
                            </label>
                            <DatePicker
                              id="employee-edit-instantDate"
                              name="instantDate"
                              className="form-control w-100"
                              selected={formData.instantDate}
                              onChange={handleInstantDateChange}
                              dateFormat="dd-MM-yyyy"
                              placeholderText="dd-MM-yyyy"
                              locale="en-AU"
                            />
                          </div>
                        </div>
                      )}
                      {formData.periodType === "durationPeriod" && (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label htmlFor="employee-edit-periodStartDate" className="form-label">
                              Period start
                            </label>
                            <DatePicker
                              id="employee-edit-periodStartDate"
                              name="periodStartDate"
                              className="form-control w-100"
                              selected={formData.periodStartDate}
                              onChange={handlePeriodStartDateChange}
                              dateFormat="dd-MM-yyyy"
                              placeholderText="dd-MM-yyyy"
                              locale="en-AU"
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="employee-edit-periodEndDate" className="form-label">
                              Period end
                            </label>
                            <DatePicker
                              id="employee-edit-periodEndDate"
                              name="periodEndDate"
                              className="form-control w-100"
                              selected={formData.periodEndDate}
                              onChange={handlePeriodEndDateChange}
                              dateFormat="dd-MM-yyyy"
                              placeholderText="dd-MM-yyyy"
                              locale="en-AU"
                            />
                          </div>
                        </div>
                      )}
                      {formData.periodType === "financialTimeFrame" && (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <FinancialYearField
                              id="employee-edit-financialYear"
                              label="Financial year"
                              value={formData.financialYear}
                              onChange={(financialYear) =>
                                setFormData((prev: any) => ({
                                  ...prev,
                                  financialYear,
                                }))
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="employee-edit-financialPeriod" className="form-label">
                              Financial period
                            </label>
                            <select
                              id="employee-edit-financialPeriod"
                              name="financialPeriod"
                              className="form-select"
                              value={formData.financialPeriod}
                              onChange={handleInputChange}
                            >
                              <option value="" disabled>
                                Select Financial Period
                              </option>
                              <option value="qtr1">Quarter 1</option>
                              <option value="qtr2">Quarter 2</option>
                              <option value="qtr3">Quarter 3</option>
                              <option value="qtr4">Quarter 4</option>
                              <option value="january">January</option>
                              <option value="february">February</option>
                              <option value="march">March</option>
                              <option value="april">April</option>
                              <option value="may">May</option>
                              <option value="june-july">{"July - June"}</option>
                              <option value="august">August</option>
                              <option value="september">September</option>
                              <option value="october">October</option>
                              <option value="november">November</option>
                              <option value="december">December</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-wrap justify-content-end gap-2 pt-3 mt-1 border-top employee-job-form-actions">
                  <button type="submit" className="btn btn-primary px-4">
                    Save changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Log Hours Modal */}

      <div
        className="modal fade employee-task-modal"
        id="exampleModalTogglee"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Log Hours
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                ref={logHoursModalCloseRef}
              ></button>
            </div>
            <div className="modal-body">
              <form
                className="d-flex flex-column"
                onSubmit={
                  isDaniyalUserId(user?.user_id, DANIYAL)
                    ? handleSubTaskHoursSubmit
                    : handleHoursSubmit
                }
              >
                <div className="mb-3">
                  <label htmlFor="taskId">Job ID:</label>
                  <input
                    type="text"
                    id="projectId"
                    name="projectId"
                    value={task?.projectId}
                    onChange={handleInputHours}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="endDate" className="form-label">
                    Date: <span className="text-danger">*</span>
                  </label>
                  <DatePicker
                    id="endDate"
                    name="endDate"
                    className="form-control w-100" // Ensure the input takes the full width
                    selected={hoursLogged.date} // Control the date in the input field
                    onChange={handleLoggedDateChange} // Pass the date directly
                    dateFormat="dd-MM-yyyy" // Australian date format
                    placeholderText="dd-MM-yyyy" // Placeholder in Australian format
                    locale="en-AU" // Use Australian locale
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="timeHrsApplied">
                    Time (Hours): <span className="text-danger">*</span>
                  </label>
                  <input
                    min="0"
                    type="number"
                    id="timeHrsApplied"
                    name="timeHrsApplied"
                    value={hoursLogged.timeHrsApplied}
                    onChange={handleInputHours}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="timeMinsApplied">Time (Minutes):</label>
                  <input
                    min="0"
                    max="59"
                    type="number"
                    id="timeMinsApplied"
                    name="timeMinsApplied"
                    value={hoursLogged.timeMinsApplied}
                    onChange={handleInputHours}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="logNotes">Notes:</label>
                  <textarea
                    id="logNotes"
                    name="logNotes"
                    value={hoursLogged.logNotes}
                    onChange={handleInputHours}
                  />
                </div>

                <button type="submit" className="btn btn-primary mt-3">
                  Submit
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Log Hours Modal */}

      <div
        className="modal fade employee-task-modal"
        id="editLogHoursModal"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Log Hours
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                ref={editLogHoursModalCloseRef}
              ></button>
            </div>
            <div className="modal-body">
              <form
                className="d-flex flex-column"
                onSubmit={handleEditLogHours}
              >
                <div className="mb-3">
                  <label htmlFor="projectId">Job ID:</label>
                  <input
                    type="text"
                    id="projectId"
                    name="projectId"
                    value={selectedLog?.projectId}
                    onChange={handleInputHours}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <span className="form-label d-block">Status</span>
                  {(() => {
                    const label = completionStatusLabel(
                      selectedLog?.status,
                      selectedLog?.statusBoolean,
                    );
                    if (label === "—") {
                      return <span className="text-muted">—</span>;
                    }
                    const done = label === "Completed";
                    return (
                      <span
                        className={`badge rounded-pill ${
                          done
                            ? "text-bg-success"
                            : "text-bg-warning text-dark"
                        }`}
                      >
                        {label}
                      </span>
                    );
                  })()}
                </div>
                <div className="mb-3">
                  <label htmlFor="endDate" className="form-label">
                    Date: <span className="text-danger">*</span>
                  </label>
                  <DatePicker
                    id="endDate"
                    name="date"
                    className="form-control w-100"
                    selected={selectedLog.date}
                    onChange={handleSelectedLogDateChange}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-MM-yyyy"
                    locale="en-AU"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="timeHrsApplied">
                    Time (Hours): <span className="text-danger">*</span>
                  </label>
                  <input
                    min="0"
                    type="number"
                    id="timeHrsApplied"
                    name="timeHrsApplied"
                    value={selectedLog.timeHrsApplied}
                    onChange={handleInputHours}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="timeMinsApplied">
                    Time (Minutes): <span className="text-danger">*</span>
                  </label>
                  <input
                    min="0"
                    max="59"
                    type="number"
                    id="timeMinsApplied"
                    name="timeMinsApplied"
                    value={selectedLog.timeMinsApplied}
                    onChange={handleInputHours}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="logNotes">Notes:</label>
                  <textarea
                    id="logNotes"
                    name="notes"
                    value={selectedLog.notes}
                    onChange={handleInputHours}
                  />
                </div>

                <button type="submit" className="btn btn-primary mt-3">
                  Submit
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* Weekly Report Modal*/}

      <div
        className="modal fade employee-task-modal"
        id="weeklyReport"
        aria-hidden="true"
        aria-labelledby="weeklyReport"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Generate Report
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => {
                  setReport("");
                  setReportGenerating(false);
                }}
              ></button>
            </div>
            <div className="modal-body">
              {reportGenerating ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
                  <div
                    className="spinner-border text-primary"
                    style={{ width: "2.75rem", height: "2.75rem" }}
                    role="status"
                    aria-label="Generating report"
                  >
                    <span className="visually-hidden">Generating report…</span>
                  </div>
                  <p className="text-muted small mb-0">Generating report…</p>
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: report ?? "" }} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Report Choice Modal*/}

      <div
        className="modal fade employee-task-modal"
        id="weeklyReportChoice"
        aria-hidden="true"
        aria-labelledby="weeklyReportChoice"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Generate Report
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => {
                  setReport("");
                  setReportGenerating(false);
                }}
              />
            </div>

            <div className="modal-body">
              <div className="mb-3 d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                  data-bs-target="#weeklyReportDateModal"
                  data-bs-toggle="modal"
                >
                  By Date
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                  data-bs-target="#weeklyReportPeriodModal"
                  data-bs-toggle="modal"
                >
                  By Period
                </button>
              </div>

              <div dangerouslySetInnerHTML={{ __html: report }} />
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Report Date Modal */}
      <div
        className="modal fade employee-task-modal"
        id="weeklyReportDateModal"
        aria-hidden="true"
        aria-labelledby="weeklyReportDateModal"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Enter Report Date
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form
                onSubmit={handleWeeklyReportSubmit}
                className="d-flex flex-column"
              >
                {/* Start Date */}
                <div className="mb-3">
                  <label htmlFor="startDate" className="form-label">
                    Start Date:
                  </label>
                  <DatePicker
                    id="startDate"
                    name="startDate"
                    className="form-control w-100"
                    selected={formData.startDate}
                    onChange={handleStartDateChange}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-MM-yyyy"
                    locale="en-AU"
                  />
                </div>

                {/* End Date */}

                <div className="mb-3">
                  <label htmlFor="endDate" className="form-label">
                    End Date:
                  </label>
                  <DatePicker
                    id="endDate"
                    name="endDate"
                    className="form-control w-100"
                    selected={formData.endDate}
                    onChange={handleDateChange}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-MM-yyyy"
                    locale="en-AU"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="btn btn-primary mt-3"
                  data-bs-dismiss="modal"
                  data-bs-target="#weeklyReport"
                  data-bs-toggle="modal"
                >
                  Generate Report
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Report Period Modal */}
      <div
        className="modal fade employee-task-modal"
        id="weeklyReportPeriodModal"
        aria-hidden="true"
        aria-labelledby="weeklyReportDateModal"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Enter Report Details
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            <div className="modal-body">
              <form
                onSubmit={handleWeeklyReportSubmit}
                className="d-flex flex-column"
              >
                {/* Period Type Selection */}
                <div className="mb-3">
                  <label htmlFor="periodType" className="form-label">
                    Type:
                  </label>
                  <select
                    id="periodType"
                    name="periodType"
                    className="form-control"
                    value={formData.periodType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="" disabled>
                      Select Type
                    </option>
                    {/* <option value="durationPeriod">Duration Period</option> */}
                    <option value="financialTimeFrame">
                      Financial Time Frame Association
                    </option>
                    {/* <option value="instant">Instant</option> */}
                  </select>
                </div>

                {/* Duration Period Fields */}
                {/* {formData.periodType === "durationPeriod" && (
                  <>
                    <div className="mb-3">
                      <label htmlFor="periodStartDate" className="form-label">
                        Start Date:
                      </label>
                      <DatePicker
                        id="periodStartDate"
                        name="periodStartDate"
                        className="form-control w-100"
                        selected={formData.periodStartDate}
                        onChange={handlePeriodStartDateChange}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="dd-MM-yyyy"
                        locale="en-AU"
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="periodEndDate" className="form-label">
                        End Date:
                      </label>
                      <DatePicker
                        id="periodEndDate"
                        name="periodEndDate"
                        className="form-control w-100"
                        selected={formData.periodEndDate}
                        onChange={handlePeriodEndDateChange}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="dd-MM-yyyy"
                        locale="en-AU"
                      />
                    </div>
                  </>
                )} */}

                {/* Instant Period Fields */}
                {/* {formData.periodType === "instant" && (
                  <div className="mb-3">
                    <label htmlFor="instantDate" className="form-label">
                      Instant Date:
                    </label>
                    <DatePicker
                      id="instantDate"
                      name="instantDate"
                      className="form-control w-100"
                      selected={formData.instantDate}
                      onChange={handleInstantDateChange}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="dd-MM-yyyy"
                      locale="en-AU"
                    />
                  </div>
                )} */}

                {/* Financial Time Frame Fields */}
                {formData.periodType === "financialTimeFrame" && (
                  <>
                    <div className="mb-3">
                      <FinancialYearField
                        id="financialYear"
                        label="Financial Year:"
                        value={formData.financialYear}
                        onChange={(financialYear) =>
                          setFormData((prev: any) => ({
                            ...prev,
                            financialYear,
                          }))
                        }
                        selectClassName="form-control"
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="financialPeriod" className="form-label">
                        Financial Period:
                      </label>
                      <select
                        id="financialPeriod"
                        name="financialPeriod"
                        className="form-control"
                        value={formData.financialPeriod}
                        onChange={handleInputChange}
                      >
                        <option value="" disabled>
                          Select Financial Period
                        </option>
                        <option value="qtr1">Quarter 1</option>
                        <option value="qtr2">Quarter 2</option>
                        <option value="qtr3">Quarter 3</option>
                        <option value="qtr4">Quarter 4</option>
                        <option value="january">January</option>
                        <option value="february">February</option>
                        <option value="march">March</option>
                        <option value="april">April</option>
                        <option value="may">May</option>
                        <option value="june-july">{"July - June"}</option>
                        <option value="august">August</option>
                        <option value="september">September</option>
                        <option value="october">October</option>
                        <option value="november">November</option>
                        <option value="december">December</option>
                      </select>
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  className="btn btn-primary mt-3"
                  data-bs-dismiss="modal"
                  data-bs-target="#weeklyReport"
                  data-bs-toggle="modal"
                >
                  Generate Report
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Add Subtask  Modal */}

      <div
        className="modal fade employee-task-modal"
        id="addSubtaskModal"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Add SubJob
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                ref={subtaskModalRef}
              ></button>
            </div>
            <div className="modal-body">
              <form className="d-flex flex-column" onSubmit={handleSubtaskSubmit}>
                <SubjobAddModalForm
                  subTask={subTask}
                  parentJobRow={task}
                  handleInputHours={handleInputHours}
                  handleSubtaskCreatedDateChange={handleSubtaskCreatedDateChange}
                  setAddSubtaskRepoHashes={setAddSubtaskRepoHashes}
                />
                <button type="submit" className="btn btn-primary mt-3">
                  Submit
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Subtask  Modal */}

      <div
        className="modal fade employee-task-modal"
        id="editSubtaskModal"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Edit SubJob
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                ref={editSubtaskModalCloseRef}
              ></button>
            </div>
            <div className="modal-body">
              <form className="d-flex flex-column" onSubmit={handleSubTaskEdit}>
                <SubjobEditModalForm
                  accordionDomId="editSubtaskCommitAccordion"
                  formIdSuffix=""
                  editSubtask={editSubtask}
                  parentRowForGithub={editSubtaskParentJob ?? editSubtask}
                  handleInputHours={handleInputHours}
                  setEditSubtaskRepoHashes={setEditSubtaskRepoHashes}
                />
                <button type="submit" className="btn btn-primary mt-3">
                  Submit
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade employee-task-modal"
        id="exampleModalToggle3"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Edit SubJob
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form className="d-flex flex-column" onSubmit={handleSubTaskEdit}>
                <SubjobEditModalForm
                  accordionDomId="editSubtaskCommitAccordionAlt1"
                  formIdSuffix="-alt1"
                  editSubtask={editSubtask}
                  parentRowForGithub={editSubtaskParentJob ?? editSubtask}
                  handleInputHours={handleInputHours}
                  setEditSubtaskRepoHashes={setEditSubtaskRepoHashes}
                />
                <button type="submit" className="btn btn-primary mt-3">
                  Submit
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade employee-task-modal"
        id="subTaskLogsModal"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                SubJob Logs
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="text-muted mb-3">
                Sub job time logs are listed under the <strong>Logged Hours</strong>{" "}
                tab.
              </p>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade employee-task-modal"
        id="showSubtaskLogs"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                SubJob Logs
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => {
                  setReport("");
                  setReportGenerating(false);
                }}
              ></button>
            </div>
            <div className="modal-body">
              <div dangerouslySetInnerHTML={{ __html: report }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Employee;
